import { AppThunk } from '@store/types';
import { setDemoOperations, clearDemoOperations } from '@reducers/v2/planboard';
import { injectDemoMachine, removeDemoMachine } from '@reducers/machines';
import { OperationSearchItem } from '@interfaces/search';
import type { DemoTourConfig } from '@components/tours/types';
import { DEMO_MACHINE_ID, DEMO_OPERATION_IDS, LEGACY_DEMO_MACHINE_ID } from './constants';

// Demo machine for the planboard
const demoMachine = {
  machineId: DEMO_MACHINE_ID,
  id: DEMO_MACHINE_ID,
  shortName: 'Demo CNC-01',
  fullName: 'Demo CNC Machine 01',
  machineStatus: {
    status: 'Active',
    isConnected: true,
  },
  // Required for NoMachineColumnFilter component
  machineGroups: [],
  operations: [],
};

// Create demo operations
const createDemoOperation = (
  id: number,
  partNumber: string,
  status: string,
  scheduledOnMachineId: number | null,
  options: Partial<OperationSearchItem> = {}
): Partial<OperationSearchItem> => ({
  productionOperationId: id,
  operationId: id,
  partNumber,
  partDescription: `Demo part for tour - ${partNumber}`,
  status,
  productionOrderNumber: `DEMO-PO-${id % 1000}`,
  productionBatchNumber: `DEMO-BATCH-${id % 1000}`,
  productionBatchId: id,
  productionOrderId: id,
  operationNumber: `OP${id % 100}`,
  operationName: `Demo Operation ${id % 100}`,
  productionOperationNumber: `DEMO-${id % 1000}`,
  scheduledOnMachineId,
  scheduledOnMachineName: scheduledOnMachineId === DEMO_MACHINE_ID ? 'Demo CNC-01' : null,
  quantityTarget: 100,
  quantityProduced: status === 'Running' ? 45 : 0,
  percentageComplete: status === 'Running' ? 45 : 0,
  scheduledStartDate: new Date().toISOString(),
  scheduledEndDate: new Date(Date.now() + 86400000).toISOString(), // +1 day
  isActive: true,
  sortOrder: id % 1000,
  // Prevent "Link Batch to Job" button from showing - mark batch as linked
  linkedNcFiles: [
    {
      ncFileId: id + 10000,
      ncFileName: `DEMO-NC-${id % 1000}.nc`,
      productionBatchId: id, // This prevents "Link Batch to Job" from showing
      productionBatchNumber: `DEMO-BATCH-${id % 1000}`,
      linkedOn: new Date().toISOString(),
      linkedBy: 'Demo System',
    },
  ],
  ...options,
});

// Demo operations for No Machine column (-1)
const noMachineDemoOperations: Partial<OperationSearchItem>[] = [
  createDemoOperation(DEMO_OPERATION_IDS.NO_MACHINE_1, 'DEMO-HOUSING-A', 'NotStarted', null, {
    shortDescription: 'Housing component - unassigned',
  }),
  createDemoOperation(DEMO_OPERATION_IDS.NO_MACHINE_2, 'DEMO-BRACKET-B', 'NotStarted', null, {
    shortDescription: 'Bracket assembly - pending assignment',
  }),
];

// Demo operations for Demo Machine column
const demoMachineOperations: Partial<OperationSearchItem>[] = [
  createDemoOperation(DEMO_OPERATION_IDS.DEMO_MACHINE_1, 'DEMO-SHAFT-C', 'Running', DEMO_MACHINE_ID, {
    shortDescription: 'Shaft machining in progress',
    elapsedTimeSeconds: 3600, // 1 hour
    operationTimeSeconds: 7200, // 2 hours total
  }),
  createDemoOperation(DEMO_OPERATION_IDS.DEMO_MACHINE_2, 'DEMO-GEAR-D', 'NotStarted', DEMO_MACHINE_ID, {
    shortDescription: 'Gear cutting - priority override',
    hasPriorityOverride: true,
  }),
];

/**
 * Inject demo data for the planboard tour
 */
export const injectPlanboardDemo = (): AppThunk => (dispatch, getState) => {
  const state = getState();

  // Get existing No Machine column data
  const existingNoMachine = state.planboard?.machineProductionOperations?.[-1] || {
    items: [],
    total: 0,
    loading: false,
  };

  // Inject demo machine into machines list
  dispatch(injectDemoMachine(demoMachine));

  // Inject demo operations
  dispatch(
    setDemoOperations([
      {
        machineId: -1,
        data: {
          items: [...noMachineDemoOperations, ...existingNoMachine.items] as OperationSearchItem[],
          total: existingNoMachine.total + noMachineDemoOperations.length,
          loading: false,
        },
      },
      {
        machineId: DEMO_MACHINE_ID,
        data: {
          items: demoMachineOperations as OperationSearchItem[],
          total: demoMachineOperations.length,
          loading: false,
        },
      },
    ])
  );
};

/**
 * Cleanup demo data after planboard tour ends
 */
export const cleanupPlanboardDemo = (): AppThunk => (dispatch) => {
  // Remove demo machine from machines list (both new and legacy IDs)
  dispatch(removeDemoMachine(DEMO_MACHINE_ID));
  dispatch(removeDemoMachine(LEGACY_DEMO_MACHINE_ID));

  // Clear demo operations from planboard
  dispatch(clearDemoOperations());
};

// Export demo config for registry
export const demoConfig: DemoTourConfig = {
  tourId: 'planboard-overview',
  inject: injectPlanboardDemo,
  cleanup: cleanupPlanboardDemo,
  injectDelay: 100,
};
